import { createSelector } from '@reduxjs/toolkit';

import { selectAllAppointments, selectAppointmentById } from 'FRONTEND/reducers/appointmentSlice';
import { selectAppointmentStatusFilterList, selectTypeFilterList } from 'FRONTEND/reducers/calendarSlice';
import { APPOINTMENT_STATUS_CANCELLED, APPOINTMENT_STATUS_PENDING, APPOINTMENT_STATUS_PENDING_OR_PROPOSED, APPOINTMENT_STATUS_PROPOSED, PARTICIPANT_STATUS_NEEDS_ACTION, PARTICIPANT_STATUS_TENTATIVE } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';


function formatExpertAppointments(appointment) {
  // If appointment has a secretary, then the main expert is the first one in requiredMap.
  const hasSecretary = Boolean(appointment.secretary);
  const mainAppointmentExpert = hasSecretary ? appointment.requiredMap?.[0] : appointment.requester;

  const expertAppointmentList = [{
    ...appointment,
    start: appointment.startDate,
    end: appointment.endDate,
    title: i18n._('Appt'),
    expertId: mainAppointmentExpert?.id, // TODO we should replace expertId by userId globally
  }];
  if (appointment.requiredMap) {
    expertAppointmentList.push(...Object.values(appointment.requiredMap).map((requiredExpert) => ({
      ...appointment,
      start: appointment.startDate,
      end: appointment.endDate,
      title: i18n._('Appt'),
      expertId: requiredExpert.id,
    })));
  }
  return expertAppointmentList;
}
export const selectExpertAppointmentById = createSelector(
  selectAppointmentById,
  (_, __, expertId) => expertId,
  (appointment, expertId) => ({
    ...appointment,
    expertId, // TODO: check why we need to put that in the appointment. Is it a problem that we don't handle if it's a resource/location id ?
  }),
);

// EXPERTS/EXPERT LIST APPOINTMENTS
const selectCalendarAppointments = createSelector(
  selectAllAppointments,
  (state, expertId) => expertId ?? state.expert.managedIdList,
  (state) => state.user.id,
  (appointmentList, selectedExpertId, userId) => {
    const selectedExpertList = Array.isArray(selectedExpertId) ? selectedExpertId : [selectedExpertId];
    const filteredAppointments = appointmentList.flatMap((appointment) => {
      const expertAppointmentList = formatExpertAppointments(appointment);
      return expertAppointmentList.filter((appointment) => {
        return selectedExpertList.includes(appointment.expertId) || appointment?.patient?.userId === userId;
      });
    });

    // Remove duplicates based on appointment.id
    // In case currentUser shares an appointment with another expert he manages
    const uniqueAppointments = filteredAppointments.reduce((acc, appointment) => {
      if (!acc.some(existingAppointment => existingAppointment.id === appointment.id)) {
        acc.push(appointment);
      }
      return acc;
    }, []);

    return uniqueAppointments;
  });


// RESOURCE/LOCATION APPOINTMENTS
export const selectResourceCalendarAppointments = createSelector(
  [selectAllAppointments, (_, resourceId) => resourceId],
  (appointmentList, resourceId) => appointmentList.filter((appointment) => appointment?.location?.station_id === resourceId),
);

export const selectTypeFilteredAppointments = createSelector(
  [selectTypeFilterList, selectCalendarAppointments],
  (typeFilterList, appointmentList) => {
    return appointmentList.filter((appointment) => typeFilterList.some((typeFilter) => appointment.appointmentType.toLowerCase() === typeFilter.toLowerCase()));
  },
);

export const selectResourceTypeFilteredAppointments = createSelector(
  [selectTypeFilterList, selectResourceCalendarAppointments],
  (typeFilterList, appointmentList) => appointmentList.filter((appointment) => typeFilterList.some((typeFilter) => appointment.appointmentType.toLowerCase() === typeFilter.toLowerCase())),
);

export const selectTypeFilteredAppointmentsForActor = createSelector(
  [(state) => state, (_, actor) => actor],
  (state, actor) => {
    if (!actor) {
      return selectTypeFilteredAppointments(state, state.expert.checkedList);
    }

    if (actor.actorType === 'resource') {
      return selectResourceTypeFilteredAppointments(state, actor?.id);
    }

    return selectTypeFilteredAppointments(state, actor?.id);
  },
);

const selectUpcomingAppointments = createSelector(
  selectCalendarAppointments,
  (appointmentList) => {
    const currentDate = new Date().toJSON();
    return appointmentList.filter((appointment) => appointment.endDate >= currentDate);
  },
);

export const selectExpertNextAppointment = createSelector(
  selectUpcomingAppointments,
  (_, __, currentDate) => currentDate,
  (expertAppointmentList, currentDate) => expertAppointmentList.find((appointment) => appointment.startDate >= currentDate && appointment.status !== APPOINTMENT_STATUS_CANCELLED),
);

export const selectExpertOngoingAppointment = createSelector(
  selectCalendarAppointments,
  (appointmentList) => {
    const currentDate = new Date().toJSON();
    return appointmentList.find((appointment) => appointment.startDate <= currentDate && appointment.endDate >= currentDate && appointment.status !== APPOINTMENT_STATUS_CANCELLED);
  },
);

export const selectStatusAndTypeFilteredCalendarAppointments = createSelector(
  (_state, startDate) => startDate,
  (_state, __, endDate) => endDate,
  (state) => selectAppointmentStatusFilterList(state),
  (state) => selectTypeFilteredAppointments(state),
  (startDate, endDate, statusFilterList, appointmentList) => {
    return appointmentList.filter((appointment) => {
      // Date filtering logic
      let isValidDate = false;

      if (startDate && endDate) {
        isValidDate = appointment.startDate < endDate && appointment.endDate > startDate;
      } else if (!startDate || !endDate) {
        isValidDate = true;
      }
      if (!isValidDate) {
        return false;
      }

      // Status filtering logic
      const isValidStatus = statusFilterList.some((typeFilter) => {
        if (appointment.status.toLowerCase() === typeFilter.toLowerCase()) {
          return true;
        } else if (typeFilter.toLowerCase() === APPOINTMENT_STATUS_PENDING_OR_PROPOSED && [APPOINTMENT_STATUS_PENDING, APPOINTMENT_STATUS_PROPOSED].includes(appointment.status.toLowerCase())) {
          return true;
        }
        return false;
      });

      return isValidStatus;
    });
  },
);

export const selectAllNonCancelledAppointments = createSelector(
  selectAllAppointments,
  (appointmentList) => appointmentList.filter((appointment) => appointment.status !== APPOINTMENT_STATUS_CANCELLED),
);


export const selectAppointmentsWithActionNeededCount = createSelector(
  selectAllAppointments,
  (state) => state.user.id,
  (_, currentDate) => currentDate,
  (appointmentList, userId, currentDate) => {
    return appointmentList.reduce((count, appointment) => {
      if ([PARTICIPANT_STATUS_NEEDS_ACTION, PARTICIPANT_STATUS_TENTATIVE].includes(appointment.requiredMap?.[userId]?.status) && appointment.endDate >= currentDate) {
        count += 1;
      }
      return count;
    }, 0);
  },
);
